import { Box, Button, Grid, IconButton } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Plus } from "react-feather";
import { GiftingForm } from "../../components/moduleForms/Gifting/GiftingForm";
import { AccountSummary } from "../../components/shared/AccountSummary";
import { ColouredContainer } from "../../components/shared/ColoredContainer";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { PageBlurb } from "../../components/shared/PageBlurb";
import { PageHeader } from "../../components/shared/PageHeader";
import { SideDrawer } from "../../components/shared/SideDrawer";
import { PageHeaderControlContext } from "../../context/PageHeaderControlContext";
import { useDeletePlan } from "../../hooks/plans/useDeletePlan";
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { Plan } from "../../models/accounts/plan";
import { isLoading } from "../../utils/isLoading";
import { extraordinaryColors } from "../../utils/theme";
import { useData } from "../../utils/useData";
import {
  DeletePlanButton,
  PlanStatistics,
  StyledEditIcon
} from "../allowances/Allowances";

const Gifting = () => {
  const { plans, serviceSectors, accounts, employees } = useData();

  const { setHeaderActions } = useContext(PageHeaderControlContext);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [editingPlan, setEditingPlan] = useState<Plan | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const mutationDeletePlan = useDeletePlan();

  const giftingPlans: Plan[] = useMemo(() => {
    return (
      plans.data?.items.filter(
        (plan) =>
          plan.employerModuleOptions.employerModuleType ===
          EmployerModuleType.GIFTING
      ) ?? []
    );
  }, [plans.data]);

  const planTotalUsersMap: Record<string, number> = useMemo(() => {
    const planMap: Record<string, number> = {};
    accounts.data?.items.forEach((account) => {
      planMap[account.planId] ??= 0;
      planMap[account.planId] += 1;
    });

    return planMap;
  }, [accounts.data]);

  useEffect(() => {
    setHeaderActions([
      <Button
        variant="contained"
        onClick={() => setIsAdding(true)}
        startIcon={<Plus size={16} />}
        size="medium"
        disabled={false}
      >
        New
      </Button>
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading([plans, employees, accounts, serviceSectors]))
    return <LoadingSpinner />;

  const renderPlan = (plan: Plan) => {
    return (
      <Grid item xs={12} md={6} key={plan.id}>
        <ColouredContainer
          title={plan.name}
          color={extraordinaryColors.pink.main}
          toolbarItems={
            <>
              <DeletePlanButton
                totalUsers={planTotalUsersMap[plan.id] ?? 0}
                onClick={() => mutationDeletePlan.mutate(plan)}
              />
              <IconButton
                onClick={() => {
                  setIsEditing(true);
                  setEditingPlan(plan);
                }}
              >
                <StyledEditIcon />
              </IconButton>
            </>
          }
          subtitleItems={
            <PlanStatistics totalUsers={planTotalUsersMap[plan.id] ?? 0} />
          }
        >
          <Box sx={{ padding: 2 }}>
            <AccountSummary
              variant="fullColor"
              colorOverride={extraordinaryColors.pink.main}
              planInfo={{
                ...plan,
                planType: plan.type,
                serviceSectorIds:
                  plan.controls.serviceSectors?.map((ssref) => ssref.id) ?? []
              }}
              serviceSectors={serviceSectors.data?.items}
            />
          </Box>
        </ColouredContainer>
      </Grid>
    );
  };

  return (
    <>
      <PageHeader pageTitle="Gifting" />
      <PageBlurb
        description="Create tailored gifting programs, send digital cards in seconds, and only pay for what’s used. No lost cards, no manual top-ups, and no FBT on unredeemed funds. It’s the smarter way to celebrate birthdays, milestones, and wins – without the admin."
        borderColor={extraordinaryColors.pink.main}
      />

      <Grid container spacing={2}>
        {giftingPlans
          .filter(
            (p) =>
              p.controls.purchases.isEnabled &&
              !p.controls.payments.isEnabled &&
              p.employerModuleOptions.employerModuleType ===
                EmployerModuleType.GIFTING
          )
          .map(renderPlan)}
      </Grid>
      <SideDrawer title="Add Gift" open={isAdding} setIsOpen={setIsAdding}>
        <GiftingForm onClose={() => setIsAdding(false)} />
      </SideDrawer>
      <SideDrawer title="Edit Gift" open={isEditing} setIsOpen={setIsEditing}>
        <GiftingForm plan={editingPlan!} onClose={() => setIsEditing(false)} />
      </SideDrawer>
    </>
  );
};

export { Gifting };
